import { useMemo } from 'react'
import { X } from 'lucide-react'
import Drawer from '@/components/Drawer'
import { useTranslation } from 'react-i18next'
import { Chip, IconButton, Typogaphy } from 'vnetwork-ui'
import { Color } from 'vnetwork-ui/dist/types'
import { CONTRACT_PROCESS_STATUS } from '../../constants'
import { ContractProcess } from '../../types'
import Detail from '../Detail'

interface ProcessDetailDrawerProps {
    process: ContractProcess | null | undefined
    contractID: number
    shouldCheckRegistrationDate: boolean
    onClose: () => void
}

/** Right-side drawer showing process detail with header (title + status) */
const ProcessDetailDrawer = ({
    process,
    contractID,
    shouldCheckRegistrationDate,
    onClose,
}: ProcessDetailDrawerProps) => {
    const { t } = useTranslation()

    const processStatus = useMemo(() => {
        if (!process) return null
        return CONTRACT_PROCESS_STATUS(t)?.find(
            (s) => s.value === process.contract_process_status
        )
    }, [process, t])

    return (
        <Drawer open={!!process} onClose={onClose} width="800px">
            <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <Typogaphy className="font-semibold text-gray-800 truncate">
                            {process?.contract_process_title ||
                                t('contract-process-list')}
                        </Typogaphy>
                        <span className="text-xs text-gray-400 shrink-0">
                            #{process?.contract_process_id}
                        </span>
                        {processStatus && (
                            <Chip
                                label={
                                    processStatus.label ||
                                    (process?.contract_process_status as string)
                                }
                                color={processStatus.color as Color}
                                size="small"
                            />
                        )}
                    </div>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        className="p-1 hover:bg-gray-200 rounded-full transition-colors shrink-0"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </IconButton>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {process && (
                        <Detail
                            process={process}
                            contractID={contractID}
                            shouldCheckRegistrationDate={
                                shouldCheckRegistrationDate
                            }
                        />
                    )}
                </div>
            </div>
        </Drawer>
    )
}

export default ProcessDetailDrawer
